import { useId } from "react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { CapacityMode } from "../types";

interface CapacityModeSelectorProps {
  value: CapacityMode;
  onChange: (mode: CapacityMode) => void;
  disabled?: boolean;
}

export function CapacityModeSelector({
  value,
  onChange,
  disabled = false,
}: CapacityModeSelectorProps) {
  const { t } = useTranslation("settings");
  const id = useId();
  const descriptionId = `${id}-description`;

  return (
    <SettingCard
      title={t("capacity.modeToggle.title")}
      description={t("capacity.modeToggle.description")}
      helpDetails={
        <ul className="space-y-2">
          <li>
            <span className="font-medium text-foreground">
              {t("capacity.modeToggle.help.off.title")}:
            </span>{" "}
            {t("capacity.modeToggle.help.off.description")}
          </li>
          <li>
            <span className="font-medium text-foreground">
              {t("capacity.modeToggle.help.on.title")}:
            </span>{" "}
            {t("capacity.modeToggle.help.on.description")}
          </li>
        </ul>
      }
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <Label htmlFor={id} className="text-base font-medium">
            {t("capacity.modeToggle.advanced.label")}
          </Label>
          <p id={descriptionId} className="text-sm text-muted-foreground">
            {t("capacity.modeToggle.advanced.description")}
          </p>
        </div>
        <Switch
          id={id}
          aria-describedby={descriptionId}
          checked={value === "advanced"}
          onCheckedChange={(checked) => onChange(checked ? "advanced" : "simple")}
          disabled={disabled}
        />
      </div>
    </SettingCard>
  );
}
